import { useEffect, useState } from "react";

import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";

import type { AuthClient, ProvidersResponse } from "@nav-games/auth-client";

/// Launch a provider's hosted sign-in page. On native we present it in
/// an in-app SFSafariViewController (via @capacitor/browser) so the
/// user never leaves the app — Apple rejects a full hand-off to the
/// system browser (Guideline 4). The OAuth round-trip redirects back to
/// the `goastern-nav://auth/complete` custom scheme, which App.tsx
/// catches and then closes this sheet. On web we do a normal redirect.
function openProviderSignIn(url: string) {
  if (Capacitor.isNativePlatform()) {
    void Browser.open({ url });
  } else {
    window.location.assign(url);
  }
}

interface Props {
  auth: AuthClient;
  /// Called after a successful sign-in via the paste-a-code path so
  /// the SPA can route the user past the sign-in form (typically to
  /// /play). The magic-link path doesn't use this — its session
  /// arrives via AuthCallback after the redirect.
  onSignedIn?: (to: string) => void;
}

export function SignIn({ auth, onSignedIn }: Props) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [code, setCode] = useState("");
  const [providers, setProviders] = useState<ProvidersResponse | null>(null);
  const [providersError, setProvidersError] = useState<string | null>(null);
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "sending" }
    | { kind: "sent" }
    | { kind: "redeeming" }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  const [codeError, setCodeError] = useState<string | null>(null);

  // Discover providers once. Hides buttons for anything not wired up
  // on the worker (Apple/Google/magic-link each gate on their own
  // env-var set).
  useEffect(() => {
    let cancelled = false;
    auth
      .providers()
      .then((p) => {
        if (!cancelled) setProviders(p);
      })
      .catch((err: Error) => {
        if (!cancelled) setProvidersError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim().length === 0) return;
    setState({ kind: "sending" });
    try {
      await auth.requestMagicLink({
        email: email.trim(),
        ...(displayName.trim().length > 0
          ? { displayName: displayName.trim() }
          : {}),
      });
      setState({ kind: "sent" });
    } catch (err) {
      setState({ kind: "error", message: (err as Error).message });
    }
  };

  const redeemCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) {
      setCodeError("Code must be 6 digits.");
      return;
    }
    setCodeError(null);
    const prevState = state.kind;
    setState({ kind: "redeeming" });
    try {
      await auth.redeemMagicCode(digits);
      // Session is now persisted; hand control back to the parent so
      // it can navigate past the sign-in form.
      onSignedIn?.("/play");
    } catch (err) {
      // Drop back to whichever state the user came from so the rest
      // of the screen still makes sense.
      setState({ kind: prevState === "sent" ? "sent" : "idle" });
      setCodeError((err as Error).message);
    }
  };

  /// The "I have a 6-digit code, sign me in" form. Identical UI
  /// whether shown on the idle sign-in screen (handy on iOS Simulator
  /// / second device, where the link can't be clicked) or under the
  /// "Check your email" message after the user requests a link.
  const codeForm = (
    <form onSubmit={redeemCode}>
      <label className="small muted" htmlFor="magic-code">
        6-digit code from email
      </label>
      <input
        id="magic-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="123 456"
        maxLength={9}
        style={{
          marginTop: 4,
          marginBottom: 12,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 18,
          letterSpacing: 4,
        }}
      />
      <div className="row">
        <button type="submit" disabled={state.kind === "redeeming"}>
          {state.kind === "redeeming" ? "Signing in…" : "Sign in with code"}
        </button>
        {codeError && (
          <span className="small" style={{ color: "var(--danger)" }}>
            {codeError}
          </span>
        )}
      </div>
    </form>
  );

  if (state.kind === "sent" || state.kind === "redeeming") {
    return (
      <>
        <div className="card">
          <h2>Check your email</h2>
          <p className="muted small">
            We've sent a sign-in link to <strong>{email}</strong>. Click
            it on this device to come back signed in.
          </p>
          <p className="muted small">
            If you don't see it, look in spam — or ask the operator
            whether Resend is configured on the worker (without it the
            link appears in <code>wrangler tail</code> instead).
          </p>
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Or paste the code</h3>
          <p className="muted small" style={{ marginBottom: 8 }}>
            If the email opens on a different device than this one,
            type the 6-digit code from the email here.
          </p>
          {codeForm}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="card">
        <h2>Sign in</h2>
        <p className="muted small">
          Buttons hide themselves automatically when a provider isn't
          configured on the worker yet.
        </p>

        {providers && (providers.apple || providers.google) && (
          <div
            className="row"
            style={{
              marginBottom: 16,
              flexDirection: "column",
              alignItems: "stretch",
              gap: 8,
            }}
          >
            {providers.apple && (
              <button
                type="button"
                className="secondary"
                onClick={() => openProviderSignIn(auth.appleSignInUrl())}
              >
                Continue with Apple
              </button>
            )}
            {providers.google && (
              <button
                type="button"
                className="secondary"
                onClick={() => openProviderSignIn(auth.googleSignInUrl())}
              >
                Continue with Google
              </button>
            )}
            {providers.magicLink && (
              <div
                className="muted small"
                style={{ textAlign: "center", padding: "4px 0" }}
              >
                — or —
              </div>
            )}
          </div>
        )}

        {providers && providers.magicLink && (
          <form onSubmit={submit}>
            <label className="small muted" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="skipper@example.com"
              required
              style={{ marginTop: 4, marginBottom: 12 }}
            />
            <label className="small muted" htmlFor="displayName">
              Display name (optional)
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="What should we call you?"
              style={{ marginTop: 4, marginBottom: 16 }}
            />
            <div className="row">
              <button type="submit" disabled={state.kind === "sending"}>
                {state.kind === "sending" ? "Sending…" : "Send magic link"}
              </button>
              {state.kind === "error" && (
                <span className="small" style={{ color: "var(--danger)" }}>
                  {state.message}
                </span>
              )}
            </div>
          </form>
        )}

        {providers &&
          !providers.apple &&
          !providers.google &&
          !providers.magicLink && (
            <p className="small" style={{ color: "var(--warn)" }}>
              No sign-in providers are configured on the auth worker
              yet. See <code>goastern-platform/apps/auth/DEPLOY.md</code>{" "}
              Phase 4.
            </p>
          )}

        {providersError && (
          <p className="small" style={{ color: "var(--danger)" }}>
            Couldn't reach the auth worker: {providersError}
          </p>
        )}
      </div>

      {/* Always offer the paste-a-code path. Critical for iOS Simulator
          (no Mail account), and useful for anyone whose email opens on
          a different device than the one running the app. */}
      {providers && providers.magicLink && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Got a code already?</h3>
          <p className="muted small" style={{ marginBottom: 8 }}>
            If you've already requested a sign-in email and have the
            6-digit code, paste it here.
          </p>
          {codeForm}
        </div>
      )}
    </>
  );
}
